'use client';

import { useCallback, useEffect, useState, type RefObject } from 'react';
import { useReducedMotion } from 'motion/react';

/**
 * Paging for the board's rows (spec 014).
 *
 * The gallery's hook steps one tile at a time and waits 400ms for the open
 * tile's width animation before it scrolls. The board has neither an animation
 * nor a single open tile — every tile is already shown — so an arrow press
 * moves by a viewport of tiles, immediately. The edge flags come from the
 * scroll position itself, and a ResizeObserver re-reads them when a hidden
 * lane is revealed (its width is 0 until then).
 */
export const usePageScroll = (ref: RefObject<HTMLElement | null>) => {
  const prefersReducedMotion = useReducedMotion();
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    const el = ref.current;

    if (!el) return;

    const update = () => {
      setAtStart(el.scrollLeft <= 1);
      setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
    };

    update();
    el.addEventListener('scroll', update, { passive: true });

    const observer = new ResizeObserver(update);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [ref]);

  const page = useCallback(
    (direction: -1 | 1) => {
      const el = ref.current;

      if (!el) return;

      // Nine tenths of the viewport, so the last tile seen stays as a landmark.
      el.scrollBy({ left: direction * el.clientWidth * 0.9, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    },
    [ref, prefersReducedMotion]
  );

  return {
    atStart,
    atEnd,
    prev: useCallback(() => page(-1), [page]),
    next: useCallback(() => page(1), [page]),
  };
};
